'use client'

import { useLocale } from 'next-intl'
import { Fragment, useMemo } from 'react'
import { BreadcrumbList, DayOfWeek, Restaurant, WithContext } from 'schema-dts'
import { useGetRestaurantQuery } from '@/api/restaurant/get-restaurant-query'
import { parseReadableRating } from '@/features/reviews/utils/reviews-helpers'
import { createDomainUrl, createHttpsUrl } from '@/setup/domain'
import { UrlConfig } from '@/setup/routing'
import { range } from '@/utils/functions/range'
import { createImageUrl } from '@/utils/image/create-image-url'

interface IRestaurantStructuredDataProps {
    citySlug: string
    restaurantSlug: string
}

const DAY_OF_WEEK_MAP: Record<string, DayOfWeek> = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
}

export const RestaurantStructuredData = ({
    citySlug,
    restaurantSlug,
}: IRestaurantStructuredDataProps) => {
    // Prefetched and hydrated by the server component above, so this reads from cache.
    const { data } = useGetRestaurantQuery(restaurantSlug)
    const locale = useLocale()

    const restaurant = data?.data

    const restaurantUrl = createHttpsUrl(
        createDomainUrl(
            UrlConfig['/restaurants/[citySlug]/[restaurantSlug]'].getUrl({
                citySlug,
                restaurantSlug,
            }),
            locale
        )
    )

    const jsonLd = useMemo<WithContext<Restaurant> | undefined>(() => {
        if (!restaurant) return

        const firstImage = restaurant.visuals?.filter((visual) => visual.type === 'IMAGE')[0]

        const cuisines = restaurant.tags
            ?.filter((tag) => tag.category === 'cuisine')
            .map((tag) => tag.name)

        const reviewsWithText = restaurant.reviews?.filter((review) => review.review?.trim())

        return {
            '@context': 'https://schema.org',
            '@type': 'Restaurant',
            '@id': restaurantUrl,
            name: restaurant.name,
            telephone: restaurant.phone ?? undefined,

            address: restaurant.address
                ? {
                      '@type': 'PostalAddress',
                      streetAddress: restaurant.address ?? undefined,
                      addressLocality: restaurant.city?.name,
                      postalCode: restaurant.zipCode ?? undefined,
                      addressCountry: restaurant.city?.country?.shortCode ?? undefined,
                  }
                : undefined,

            geo:
                typeof restaurant.latitude === 'number' && typeof restaurant.longitude === 'number'
                    ? {
                          '@type': 'GeoCoordinates',
                          latitude: restaurant.latitude,
                          longitude: restaurant.longitude,
                      }
                    : undefined,

            url: restaurantUrl,

            // Google recommends providing images in 16:9, 4:3 and 1:1
            image: firstImage?.visualUrl
                ? [
                      createImageUrl(firstImage.visualUrl, {
                          width: 1200,
                          aspectRatio: 16 / 9,
                          resolution: 1,
                      }),
                      createImageUrl(firstImage.visualUrl, {
                          width: 1200,
                          aspectRatio: 4 / 3,
                          resolution: 1,
                      }),
                      createImageUrl(firstImage.visualUrl, {
                          width: 1200,
                          aspectRatio: 1,
                          resolution: 1,
                      }),
                  ]
                : undefined,

            servesCuisine: cuisines?.length ? cuisines : undefined,

            openingHoursSpecification: restaurant.openingHours?.length
                ? restaurant.openingHours
                      .filter((openingHours) => DAY_OF_WEEK_MAP[openingHours.day])
                      .map((openingHours) => ({
                          '@type': 'OpeningHoursSpecification' as const,
                          dayOfWeek: DAY_OF_WEEK_MAP[openingHours.day],
                          opens: openingHours.openingTime,
                          closes: openingHours.closingTime,
                      }))
                : undefined,

            aggregateRating:
                typeof restaurant.avgRating === 'number' && Boolean(restaurant.ratingsCount)
                    ? {
                          '@type': 'AggregateRating',
                          ratingValue: parseReadableRating(
                              restaurant.avgRating,
                              restaurant.ratingsCount
                          ),
                          reviewCount: restaurant.ratingsCount,
                      }
                    : undefined,

            priceRange: restaurant.priceRange
                ? range(restaurant.priceRange).reduce(
                      (acc) => `${acc}${restaurant.city?.country?.currency?.symbol ?? '$'}`,
                      ''
                  )
                : undefined,

            review: reviewsWithText?.map((review) => {
                return {
                    '@type': 'Review',
                    author: {
                        '@type': 'Person',
                        name: review.name ?? undefined,
                    },
                    reviewRating: {
                        '@type': 'Rating',
                        ratingValue: review.rating,
                        bestRating: 5,
                        worstRating: 1,
                    },
                    datePublished: review.dateTimestamp ? String(review.dateTimestamp) : undefined,
                    description: review.review ?? undefined,
                }
            }),
        }
    }, [restaurant, restaurantUrl])

    const breadcrumbJsonLd = useMemo<WithContext<BreadcrumbList> | undefined>(() => {
        if (!restaurant?.city?.name) return

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: restaurant.city.name,
                    item: createHttpsUrl(
                        createDomainUrl(
                            UrlConfig['/restaurants/[citySlug]'].getUrl({ citySlug }),
                            locale
                        )
                    ),
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: restaurant.name,
                    item: restaurantUrl,
                },
            ],
        }
    }, [restaurant, citySlug, locale, restaurantUrl])

    return (
        <Fragment>
            {jsonLd && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(jsonLd),
                    }}
                />
            )}

            {breadcrumbJsonLd && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(breadcrumbJsonLd),
                    }}
                />
            )}
        </Fragment>
    )
}
